import { API_BASE } from '@/api/v2/client'
import { useUserStore } from '@/stores/user'

export interface StreamEvent {
  type: 'text_delta' | 'tool_call' | 'tool_result' | 'done' | 'error'
  event_id: string
  content?: string
  tool_name?: string
  tool_input?: Record<string, unknown>
  result?: string
  conversation_id?: string
  title?: string
  message?: string
}

interface StreamOptions {
  message: string
  conversationId?: string | null
  provider?: string | null
  signal?: AbortSignal
  onEvent: (event: StreamEvent) => void
  onError: (error: Error) => void
  onDone: () => void
}

function buildRequestBody(options: StreamOptions): string {
  const body: Record<string, unknown> = { message: options.message }
  if (options.conversationId) body.conversation_id = options.conversationId
  if (options.provider) body.provider = options.provider
  return JSON.stringify(body)
}

function parseSSELine(line: string): StreamEvent | null {
  if (!line.startsWith('data: ')) return null
  const data = line.slice(6).trim()
  if (!data || data === '[DONE]') return null
  try {
    return JSON.parse(data) as StreamEvent
  } catch {
    return null
  }
}

async function readStream(reader: ReadableStreamDefaultReader<Uint8Array>, options: StreamOptions) {
  const decoder = new TextDecoder()
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })
    const lines = buffer.split('\n')
    buffer = lines.pop() ?? ''

    for (const line of lines) {
      const event = parseSSELine(line)
      if (!event) continue

      options.onEvent(event)
      if (event.type === 'done') return
      if (event.type === 'error') {
        options.onError(new Error(event.message ?? 'Stream error'))
        return
      }
    }
  }
}

export function useAiStream() {
  async function streamChat(options: StreamOptions) {
    const userStore = useUserStore()
    const url = `${API_BASE}/ai/chat`

    let response: Response
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'text/event-stream',
          Authorization: `Bearer ${userStore.token}`,
        },
        body: buildRequestBody(options),
        signal: options.signal,
      })
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      options.onError(err as Error)
      return
    }

    if (!response.ok) {
      options.onError(new Error(`HTTP ${response.status}: ${response.statusText}`))
      return
    }

    const reader = response.body?.getReader()
    if (!reader) {
      options.onError(new Error('No response body'))
      return
    }

    try {
      await readStream(reader, options)
    } catch (err) {
      if ((err as Error).name === 'AbortError') return
      options.onError(err as Error)
    } finally {
      reader.releaseLock()
      options.onDone()
    }
  }

  return { streamChat }
}
